import { Box, Button, Divider, Paper, Stack } from '@mui/material';
import { formatarDinheiro, formatarFatura, getTipoStatus, parseData } from '../../helper';
import contaReceberApi from '../../api/apiContaReceber';
import { toast } from 'react-toastify';
import { DUPR_STATUS_EM_ABERTO, DUPR_STATUS_PAGO, DUPR_STATUS_PARCIAL } from '../../constants';
import {
  DadosListagemContasReceberCliente,
  ParamsKeyContaReceber
} from '../../types/ContaReceberTypes';
import { useSessao } from '../../redux/sessao/sessaoHooks';
import PrintIcon from '@mui/icons-material/Print';
import Typography from '@mui/material/Typography';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import dayjs from 'dayjs';
import { colors } from '../../theme';

type Props = {
  contaReceber: DadosListagemContasReceberCliente;
  onSetLoading: (loading: boolean) => void;
  onSetDadosSelecionados: (contaReceber: DadosListagemContasReceberCliente | null) => void;
  onSetOpenModalConfimarBoleto: (open: boolean) => void;
};

const InfoBlock: React.FC<{
  label: string;
  value: React.ReactNode;
  valueColor?: string;
  valueBold?: boolean;
  align?: 'left' | 'right' | 'center';
}> = ({ label, value, valueColor, valueBold, align = 'left' }) => (
  <Box sx={{ textAlign: align, minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
      {label}
    </Typography>
    <Typography
      variant="body2"
      noWrap
      sx={{ fontWeight: valueBold ? 700 : 500, color: valueColor ?? 'text.primary' }}
    >
      {value}
    </Typography>
  </Box>
);

const CardContaReceberMobile: React.FC<Props> = (props) => {
  const { contaReceber, onSetLoading, onSetDadosSelecionados, onSetOpenModalConfimarBoleto } =
    props;
  const [sessao] = useSessao();

  const vencido =
    contaReceber.status !== DUPR_STATUS_PAGO &&
    parseInt(contaReceber.vencimento) < parseInt(dayjs().format('YYYYMMDD'));

  const gerarRecibo = async (contaReceber: DadosListagemContasReceberCliente) => {
    try {
      onSetLoading(true);

      if (!contaReceber) return;

      const params: ParamsKeyContaReceber = {
        codigoEmpresa: contaReceber.codigoEmpresa,
        codigoCliente: contaReceber.codigoCliente,
        fatura: contaReceber.fatura,
        financeira: contaReceber.financeira
      };

      await contaReceberApi.gerarReciboBaixa(params);
    } catch (error: any) {
      toast.error('Erro ao gerar recibo.');
    } finally {
      onSetLoading(false);
    }
  };

  const podeCobrar =
    (sessao?.empresa?.temIntegracaoBoleto || sessao?.empresa?.temIntegracaoPix) &&
    contaReceber.status !== DUPR_STATUS_PAGO &&
    !contaReceber.aguardandoConciliacao;

  const podeEmitirRecibo = contaReceber.status !== DUPR_STATUS_EM_ABERTO;

  const temAcoes = podeCobrar || podeEmitirRecibo;

  return (
    <Paper
      elevation={2}
      sx={{
        my: 1.5,
        px: 2.5,
        py: 2,
        borderRadius: 3,
        width: '100%',
        boxSizing: 'border-box',
        backgroundColor: '#fff',
        borderLeft: `4px solid ${
          vencido
            ? colors.red
            : contaReceber.status === DUPR_STATUS_PAGO
              ? colors.green
              : colors.blue
        }`
      }}
    >
      <Stack spacing={1.5}>
        {/* Linha 1: Empresa + Status */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" fontWeight={500} noWrap>
            {formatarFatura(contaReceber.fatura)}
          </Typography>
          {/* <Typography variant="body2" fontWeight={500} sx={{ flex: 1, mr: 1 }} noWrap>
            {contaReceber.empresa?.nome}
          </Typography> */}
          <Box sx={{ flexShrink: 0 }}>{getTipoStatus(contaReceber)}</Box>
        </Box>

        {/* Linha 2: Valor + Vencimento + Parcela (blocos horizontais) */}
        <Box
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 2 }}
        >
          {contaReceber.status === DUPR_STATUS_PAGO && (
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                Vr. Pago
              </Typography>
              <Typography
                variant="h6"
                fontWeight={600}
                color="text.primary"
                sx={{ lineHeight: 1.2 }}
              >
                {formatarDinheiro(contaReceber.valores.valorJaPago)}
              </Typography>
            </Box>
          )}

          {(contaReceber.status === DUPR_STATUS_PARCIAL ||
            contaReceber.status === DUPR_STATUS_EM_ABERTO) && (
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                {contaReceber.status === DUPR_STATUS_PARCIAL ? 'Vr. Restante' : 'Valor'}
              </Typography>
              <Typography
                variant="h6"
                fontWeight={600}
                color="text.primary"
                sx={{ lineHeight: 1.2 }}
              >
                {formatarDinheiro(contaReceber.valores.restante)}
              </Typography>
            </Box>
          )}

          {(contaReceber.status === DUPR_STATUS_PARCIAL ||
            contaReceber.status === DUPR_STATUS_EM_ABERTO) && (
            <InfoBlock
              label="Vencimento"
              value={parseData(contaReceber.vencimento)}
              valueColor={vencido ? colors.red : undefined}
              valueBold={vencido}
              align="center"
            />
          )}

          {contaReceber.status === DUPR_STATUS_PAGO && (
            <InfoBlock
              label="Pagamento"
              value={parseData(contaReceber.dataPagamento)}
              align="center"
            />
          )}

          <InfoBlock label="Parcela" value={`${contaReceber.parcela}ª`} align="right" />
        </Box>

        {/* Linha 4: Ações lado a lado */}
        {temAcoes && (
          <>
            <Divider />
            <Box sx={{ display: 'flex', gap: 1 }}>
              {podeCobrar && (
                <Button
                  fullWidth
                  variant="contained"
                  disableElevation
                  size="small"
                  startIcon={<ReceiptLongIcon />}
                  onClick={() => {
                    onSetDadosSelecionados(contaReceber);
                    onSetOpenModalConfimarBoleto(true);
                  }}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  Pagar agora
                </Button>
              )}

              {podeEmitirRecibo && (
                <Button
                  fullWidth
                  variant="outlined"
                  size="small"
                  startIcon={<PrintIcon />}
                  onClick={() => {
                    gerarRecibo(contaReceber);
                  }}
                  sx={{ textTransform: 'none' }}
                >
                  Imprimir recibo
                </Button>
              )}
            </Box>
          </>
        )}
      </Stack>
    </Paper>
  );
};

export default CardContaReceberMobile;
